import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import {isLocalDateString, todayLocalDateString} from '../../lib/date-range';
import {parseMeasurementValueCm} from '../../lib/measurement-value';
import {MEASUREMENT_VALUE_MESSAGE} from './measurement-value.validator';

/** Form model of the session sheet: one string per enabled metric, keyed by metric id. */
export type MeasurementSessionFormValues = {
  readonly measuredOn: string;
  readonly values: Record<string, string>;
};

const isEmptyOrMeasurement = (value: string): boolean =>
  value.trim() === '' || parseMeasurementValueCm(value) !== null;

/**
 * Field-level rules only: a real calendar day that is not after today, and
 * every typed value in range. Whether anything changed is decided at submit.
 */
export class MeasurementSessionValidator implements Validator<MeasurementSessionFormValues> {
  readonly standardSchema = z.object({
    measuredOn: z
      .string()
      .refine(isLocalDateString, 'Pick a measurement date.')
      // Read the clock inside the refinement so tests can freeze it.
      .refine(value => value <= todayLocalDateString(), 'The date cannot be in the future.'),
    values: z.record(
      z.string(),
      z.string().refine(isEmptyOrMeasurement, MEASUREMENT_VALUE_MESSAGE),
    ),
  });

  validate(value: unknown): Result<MeasurementSessionFormValues, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(
        new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid measurement session.'),
      );
    }
    return ok(parsed.data);
  }
}
